import { useState } from "preact/hooks";
import { useResource } from "@tenergy/web-core/dashboard";
import { adminRequest } from "../api/request.js";
import type { AdminIdentity } from "../api/types.js";
import { utc } from "../format.js";
import { toastError, toastSuccess } from "../toast.js";
import { Blank, Button, ErrorNote, Panel, Pill, ScreenHeader, Spinner, Table, Td, Th, inputClass } from "../ui.js";

/**
 * Admins (docs/product/admin-auth.md §1, §5): who may use this panel, in the panel's own table — never the customers'.
 *
 *   root    set in the API environment, not here; manages admins, editors and viewers; freezes anyone
 *   admin   manages editors and viewers
 *
 * Adding, a role change, an unfreeze and a removal are confirmed by the confirmation key (the dialog opens by
 * itself). A freeze protects, so it is immediate: every session of that address ends at once.
 */

type ManagedRole = "viewer" | "editor" | "admin";

interface AdminUserRow {
  address: string;
  role: ManagedRole;
  added_by: string | null;
  added_at: string;
  frozen_at: string | null;
  frozen_by: string | null;
}

const ROLE_HINT: Record<ManagedRole, string> = {
  viewer: "reads every screen",
  editor: "also changes prices, venues, accounts",
  admin: "also manages viewers and editors",
};

export function AdminsScreen({ identity }: { identity: AdminIdentity }) {
  const list = useResource((signal) => adminRequest<{ root: string | null; data: AdminUserRow[] }>("/admin/users", { signal }), []);
  const [address, setAddress] = useState("");
  const [role, setRole] = useState<ManagedRole>("viewer");
  const [busy, setBusy] = useState(false);
  const root = identity.role === "root";
  const grantable: ManagedRole[] = root ? ["viewer", "editor", "admin"] : ["viewer", "editor"];
  const manages = (target: ManagedRole) => root || target !== "admin";
  const rows = list.data?.data ?? [];

  async function act(title: string, run: () => Promise<unknown>): Promise<boolean> {
    setBusy(true);
    try {
      await run();
      toastSuccess(title);
      list.reload();
      return true;
    } catch (error) {
      toastError(`${title}: not done`, error);
      return false;
    } finally {
      setBusy(false);
    }
  }

  const path = (target: string, tail = "") => `/admin/users/${encodeURIComponent(target)}${tail}`;

  return (
    <>
      <ScreenHeader
        title="Admins"
        lead="Who can sign in to this panel. Separate from customer accounts: a wallet here has no rights on the dashboard, and the other way round."
      />
      <div class="grid grid-cols-[minmax(0,1fr)] gap-3">
        <Panel title="Add an admin" subtitle="A TRON or Ethereum address. The change is confirmed with your confirmation key.">
          <form
            class="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto_auto]"
            onSubmit={(event) => {
              event.preventDefault();
              const target = address.trim();
              if (target === "") return;
              void act("Admin added", () => adminRequest("/admin/users", { method: "POST", body: { address: target, role } })).then((done) => {
                if (done) setAddress("");
              });
            }}
          >
            <input
              class={`${inputClass} num`}
              aria-label="Address"
              placeholder="T… or 0x…"
              value={address}
              spellcheck={false}
              onInput={(event) => setAddress((event.currentTarget as HTMLInputElement).value)}
            />
            <select
              class={`${inputClass} w-auto`}
              aria-label="Role"
              value={role}
              onChange={(event) => setRole((event.currentTarget as HTMLSelectElement).value as ManagedRole)}
            >
              {grantable.map((option) => (
                <option key={option} value={option}>
                  {option} — {ROLE_HINT[option]}
                </option>
              ))}
            </select>
            <Button type="submit" disabled={busy || address.trim() === ""}>
              Add
            </Button>
          </form>
        </Panel>

        <Panel title="Admins" subtitle={list.data?.root ? `Root (from the API environment): ${list.data.root}` : undefined}>
          {list.loading ? (
            <Spinner />
          ) : list.error !== null ? (
            <ErrorNote error={list.error} onRetry={() => list.reload()} />
          ) : (
            <Table
              rows={rows.length}
              empty="No admin yet besides root."
              head={
                <tr>
                  <Th>Address</Th>
                  <Th>Role</Th>
                  <Th>Added</Th>
                  <Th>State</Th>
                  <Th>{""}</Th>
                </tr>
              }
            >
              {rows.map((row) => {
                const mine = manages(row.role);
                return (
                  <tr key={row.address}>
                    <Td mono>{row.address}</Td>
                    <Td>
                      {mine ? (
                        <select
                          class={`${inputClass} w-auto min-w-[7.5rem]`}
                          aria-label={`Role of ${row.address}`}
                          value={row.role}
                          disabled={busy || row.frozen_at !== null}
                          onChange={(event) => {
                            const next = (event.currentTarget as HTMLSelectElement).value as ManagedRole;
                            void act("Role changed", () => adminRequest(path(row.address), { method: "PATCH", body: { role: next } }));
                          }}
                        >
                          {grantable.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      ) : (
                        row.role
                      )}
                    </Td>
                    <Td mono>
                      {utc(row.added_at)}
                      {row.added_by === null ? null : <span class="block text-[10.5px] text-ink-muted">by {row.added_by}</span>}
                    </Td>
                    <Td>
                      {row.frozen_at === null ? (
                        <Pill value="active" tone="good" />
                      ) : (
                        <span title={`Frozen ${utc(row.frozen_at)}${row.frozen_by ? ` by ${row.frozen_by}` : ""}`}>
                          <Pill value="frozen" tone="bad" />
                        </span>
                      )}
                    </Td>
                    <Td>
                      {mine || root ? (
                        <div class="flex justify-end gap-1.5 whitespace-nowrap">
                          {row.frozen_at === null ? (
                            <Button tone="danger" disabled={busy} onClick={() => void act("Frozen", () => adminRequest(path(row.address, "/freeze"), { method: "POST", body: {} }))}>
                              Freeze
                            </Button>
                          ) : (
                            <Button tone="ghost" disabled={busy} onClick={() => void act("Unfrozen", () => adminRequest(path(row.address, "/unfreeze"), { method: "POST", body: {} }))}>
                              Unfreeze
                            </Button>
                          )}
                          {mine ? (
                            <Button tone="ghost" disabled={busy} onClick={() => void act("Removed", () => adminRequest(path(row.address), { method: "DELETE" }))}>
                              Remove
                            </Button>
                          ) : null}
                        </div>
                      ) : (
                        <Blank title="Only root manages an admin" />
                      )}
                    </Td>
                  </tr>
                );
              })}
            </Table>
          )}
        </Panel>
      </div>
    </>
  );
}
